import React, { useEffect, useState } from "react";
import { GoogleMap, Marker, Polyline, InfoWindow } from "@react-google-maps/api";
import { FaMapMarkerAlt, FaPlus, FaMinus } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useGoogleMaps } from "../../context/GoogleMapsContext";

const mapContainerStyle = {
  width: "100%",
  height: "100%",
};

const defaultCenter = {
  lat: 23.1815,
  lng: 79.9864,
};

export default function RideMapCard({ booking }) {
  const navigate = useNavigate();
  const [mapZoom, setMapZoom] = useState(12);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [routeCoordinates, setRouteCoordinates] = useState([]);
  const [directionsInfo, setDirectionsInfo] = useState(null);
  const [pickupCoords, setPickupCoords] = useState(null);
  const [dropCoords, setDropCoords] = useState(null);
  const [viaCoordsList, setViaCoordsList] = useState([]);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [isLoadingRoute, setIsLoadingRoute] = useState(false);
  const [mapInstance, setMapInstance] = useState(null);

  const { isLoaded, loadError, geocoder } = useGoogleMaps();

  // Helper to parse via cities from any format (array or pipe/comma separated string)
  const parseViaCities = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) {
      return raw
        .map((c) => (typeof c === "string" ? c.trim() : (c?.name || c?.address || "")).trim())
        .filter(Boolean);
    }
    if (typeof raw === "string") {
      const str = raw.trim();
      if (!str) return [];
      if (str.includes("|")) {
        return str.split("|").map((s) => s.trim()).filter(Boolean);
      }
      return [str];
    }
    return [];
  };

  // Geocode addresses to get coordinates
  useEffect(() => {
    if (!isLoaded || !window.google || !geocoder) return;

    const geocodeAddress = async (address) => {
      return new Promise((resolve) => {
        geocoder.geocode({ address }, (results, status) => {
          if (status === "OK" && results && results[0]) {
            const { lat, lng } = results[0].geometry.location;
            resolve({ lat: lat(), lng: lng() });
          } else {
            resolve(null);
          }
        });
      });
    };

    const getCoordinates = async () => {
      setIsLoadingRoute(true);
      const pickupAddr = booking?.pickupAddress || booking?.from || "India";
      const dropAddr = booking?.dropAddress || booking?.to || "India";
      const viaCities = parseViaCities(booking?.viaCities || booking?.via);

      const pickup = await geocodeAddress(pickupAddr);
      const drop = await geocodeAddress(dropAddr);

      const viaResults = [];
      for (let i = 0; i < viaCities.length; i++) {
        const cityName = viaCities[i];
        const coords = await geocodeAddress(cityName);
        if (coords) {
          viaResults.push({
            ...coords,
            name: cityName,
            stopIndex: i + 1,
          });
        }
      }

      setViaCoordsList(viaResults);

      if (pickup && drop) {
        setPickupCoords(pickup);
        setDropCoords(drop);

        // Center map to middle of entire route
        const allPoints = [pickup, ...viaResults, drop];
        const avgLat = allPoints.reduce((sum, p) => sum + p.lat, 0) / allPoints.length;
        const avgLng = allPoints.reduce((sum, p) => sum + p.lng, 0) / allPoints.length;
        setMapCenter({ lat: avgLat, lng: avgLng });

        // Get directions with intermediate waypoints
        getDirections(pickup, drop, viaResults);
      }
      setIsLoadingRoute(false);
    };

    getCoordinates();
  }, [isLoaded, geocoder, booking]);

  // Get directions and route information with intermediate waypoints
  const getDirections = (origin, destination, viaStops = []) => {
    if (!window.google) return;

    const directionsService = new window.google.maps.DirectionsService();

    const waypoints = (viaStops || []).map((stop) => ({
      location: new window.google.maps.LatLng(stop.lat, stop.lng),
      stopover: true,
    }));

    directionsService.route(
      {
        origin,
        destination,
        waypoints,
        optimizeWaypoints: false,
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          const route = result.routes[0];

          // Extract polyline coordinates from all route legs
          const polylinePoints = [];
          let totalDistance = 0;
          let totalDuration = 0;

          route.legs.forEach((leg) => {
            if (leg.distance) totalDistance += leg.distance.value;
            if (leg.duration) totalDuration += leg.duration.value;

            leg.steps.forEach((step) => {
              polylinePoints.push({
                lat: step.start_location.lat(),
                lng: step.start_location.lng(),
              });
            });
            polylinePoints.push({
              lat: leg.end_location.lat(),
              lng: leg.end_location.lng(),
            });
          });

          setRouteCoordinates(polylinePoints);

          const distanceInKm = (totalDistance / 1000).toFixed(0);
          const totalDistanceText = `${distanceInKm} km`;

          let totalDurationText = "N/A";
          if (totalDuration > 0) {
            const hours = Math.floor(totalDuration / 3600);
            const minutes = Math.floor((totalDuration % 3600) / 60);
            totalDurationText = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
          }

          setDirectionsInfo({
            distance: totalDistanceText,
            duration: totalDurationText,
            distanceValue: totalDistance,
            durationValue: totalDuration,
            stopsCount: viaStops.length,
          });
        } else {
          console.error("Directions request failed:", status);
          setDirectionsInfo({
            distance: booking?.distance ? `${booking.distance} km` : "N/A",
            duration: booking?.duration || "N/A",
            distanceValue: booking?.distance,
            durationValue: null,
            stopsCount: viaStops.length,
          });
        }
      }
    );
  };

  // Fit bounds to show entire route and all waypoints
  useEffect(() => {
    if (mapInstance && window.google) {
      const bounds = new window.google.maps.LatLngBounds();
      let hasPoints = false;
      if (pickupCoords) {
        bounds.extend(new window.google.maps.LatLng(pickupCoords.lat, pickupCoords.lng));
        hasPoints = true;
      }
      viaCoordsList.forEach((coord) => {
        bounds.extend(new window.google.maps.LatLng(coord.lat, coord.lng));
        hasPoints = true;
      });
      if (dropCoords) {
        bounds.extend(new window.google.maps.LatLng(dropCoords.lat, dropCoords.lng));
        hasPoints = true;
      }
      if (routeCoordinates.length > 0) {
        routeCoordinates.forEach((coord) => {
          bounds.extend(new window.google.maps.LatLng(coord.lat, coord.lng));
        });
        hasPoints = true;
      }
      if (hasPoints) {
        mapInstance.fitBounds(bounds, { top: 40, right: 40, bottom: 40, left: 40 });
      }
    }
  }, [mapInstance, routeCoordinates, pickupCoords, dropCoords, viaCoordsList]);

  const handleMapLoad = (map) => {
    setMapInstance(map);
  };

  const handleZoomIn = () => setMapZoom((prev) => Math.min(prev + 1, 21));
  const handleZoomOut = () => setMapZoom((prev) => Math.max(prev - 1, 3));
  const handleResetLocation = () => {
    if (mapInstance && window.google) {
      const bounds = new window.google.maps.LatLngBounds();
      let hasPoints = false;
      if (pickupCoords) {
        bounds.extend(new window.google.maps.LatLng(pickupCoords.lat, pickupCoords.lng));
        hasPoints = true;
      }
      viaCoordsList.forEach((coord) => {
        bounds.extend(new window.google.maps.LatLng(coord.lat, coord.lng));
        hasPoints = true;
      });
      if (dropCoords) {
        bounds.extend(new window.google.maps.LatLng(dropCoords.lat, dropCoords.lng));
        hasPoints = true;
      }
      if (routeCoordinates.length > 0) {
        routeCoordinates.forEach((coord) => {
          bounds.extend(new window.google.maps.LatLng(coord.lat, coord.lng));
        });
        hasPoints = true;
      }
      if (hasPoints) {
        mapInstance.fitBounds(bounds, { top: 40, right: 40, bottom: 40, left: 40 });
      }
    }
  };

  if (loadError) {
    return (
      <div className="w-full h-[320px] lg:h-full bg-white rounded-3xl border border-slate-100/80 shadow-[0_8px_30px_rgb(0,0,0,0.03)] flex flex-col overflow-hidden">
        <div className="flex-1 flex items-center justify-center bg-gray-100">
          <p className="text-gray-600">Error loading map</p>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="w-full h-[320px] lg:h-full bg-white rounded-3xl border border-slate-100/80 shadow-[0_8px_30px_rgb(0,0,0,0.03)] flex flex-col overflow-hidden">
        <div className="flex-1 flex items-center justify-center bg-gray-100">
          <p className="text-gray-600">Loading map...</p>
        </div>
      </div>
    );
  }

  const statusLower = booking?.status?.toLowerCase() || "";
  const isRideStarted = [
    "inprogress",
    "in_progress",
    "ongoing",
    "started",
    "driver_arrived",
    "pickup",
    "drop",
    "assigned"
  ].includes(statusLower);

  const handleTrackRide = () => {
    const rideId = booking?.id || booking?._id;
    if (rideId) {
      navigate(`/track/${rideId}`);
    }
  };

  return (
    <div className="w-full h-[320px] lg:h-full bg-white rounded-3xl border border-slate-100/80 shadow-[0_8px_30px_rgb(0,0,0,0.03)] flex flex-col overflow-hidden transition-all duration-300">
      {/* MAP AREA */}
      <div className="flex-1 relative bg-gray-200 min-h-[220px] md:min-h-[360px] overflow-hidden">
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={mapCenter}
          zoom={mapZoom}
          onLoad={handleMapLoad}
          options={{
            disableDefaultUI: true,
            zoomControl: false,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            styles: [
              {
                featureType: "water",
                stylers: [{ color: "#dbeafe" }],
              },
              {
                featureType: "landscape",
                stylers: [{ color: "#f8fafc" }],
              },
              {
                featureType: "road.highway",
                stylers: [{ color: "#fed7aa" }],
              },
            ],
          }}
        >
          {/* Route Polyline (Dual-layered for high contrast) */}
          {routeCoordinates.length > 0 && (
            <>
              <Polyline
                path={routeCoordinates}
                options={{
                  strokeColor: "#1d4ed8",
                  strokeOpacity: 0.35,
                  strokeWeight: 9,
                  geodesic: true,
                }}
              />
              <Polyline
                path={routeCoordinates}
                options={{
                  strokeColor: "#2563eb",
                  strokeOpacity: 0.95,
                  strokeWeight: 5,
                  geodesic: true,
                }}
              />
            </>
          )}

          {/* Pickup Marker (Green) */}
          {pickupCoords && (
            <Marker
              position={pickupCoords}
              onClick={() => setSelectedMarker({
                position: pickupCoords,
                title: "Pickup Location",
                address: booking?.pickupAddressCity || booking?.from || "Pickup Location",
                type: "pickup",
              })}
              icon={{
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 13,
                fillColor: "#059669",
                fillOpacity: 1,
                strokeColor: "#ffffff",
                strokeWeight: 3.5,
              }}
              title={`Pickup: ${booking?.pickupAddressCity || booking?.from || "Pickup Location"}`}
            />
          )}

          {/* Intermediate Stops Markers (Amber with Sequence Number) */}
          {viaCoordsList.map((stop, idx) => (
            <Marker
              key={idx}
              position={{ lat: stop.lat, lng: stop.lng }}
              onClick={() => setSelectedMarker({
                position: { lat: stop.lat, lng: stop.lng },
                title: `Via Stop ${stop.stopIndex}`,
                address: stop.name,
                type: "via",
              })}
              icon={{
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 12,
                fillColor: "#f59e0b",
                fillOpacity: 1,
                strokeColor: "#ffffff",
                strokeWeight: 3,
              }}
              label={{
                text: String(stop.stopIndex),
                color: "#ffffff",
                fontWeight: "bold",
                fontSize: "11px",
              }}
              title={`Stop ${stop.stopIndex}: ${stop.name}`}
            />
          ))}

          {/* Drop Marker (Red) */}
          {dropCoords && (
            <Marker
              position={dropCoords}
              onClick={() => setSelectedMarker({
                position: dropCoords,
                title: "Drop-off Location",
                address: booking?.dropAddressCity || booking?.to || "Drop-off Location",
                type: "drop",
              })}
              icon={{
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 13,
                fillColor: "#dc2626",
                fillOpacity: 1,
                strokeColor: "#ffffff",
                strokeWeight: 3.5,
              }}
              title={`Drop: ${booking?.dropAddressCity || booking?.to || "Drop Location"}`}
            />
          )}

          {/* Marker InfoWindow Popup */}
          {selectedMarker && (
            <InfoWindow
              position={selectedMarker.position}
              onCloseClick={() => setSelectedMarker(null)}
            >
              <div className="p-1 max-w-[220px]">
                <div className={`text-[10px] font-bold uppercase tracking-wider ${
                  selectedMarker.type === "pickup" ? "text-green-600" :
                  selectedMarker.type === "drop" ? "text-red-600" : "text-amber-600"
                }`}>
                  {selectedMarker.title}
                </div>
                <div className="text-xs font-semibold text-gray-800 mt-0.5 break-words">
                  {selectedMarker.address}
                </div>
              </div>
            </InfoWindow>
          )}
        </GoogleMap>

        {/* Map Controls */}
        <div className="absolute right-4 bottom-14 flex flex-col gap-2 z-10">
          <button
            onClick={handleResetLocation}
            className="bg-white/95 backdrop-blur-sm shadow-md p-2 rounded-xl hover:bg-gray-50 transition-all text-gray-700 hover:scale-105 active:scale-95 border border-slate-100"
            title="Reset location / fit route"
          >
            <FaMapMarkerAlt size={15} className="text-[#f26a21]" />
          </button>
          <button
            onClick={handleZoomIn}
            className="bg-white/95 backdrop-blur-sm shadow-md p-2 rounded-xl hover:bg-gray-50 transition-all text-gray-700 hover:scale-105 active:scale-95 border border-slate-100"
            title="Zoom in"
          >
            <FaPlus size={13} />
          </button>
          <button
            onClick={handleZoomOut}
            className="bg-white/95 backdrop-blur-sm shadow-md p-2 rounded-xl hover:bg-gray-50 transition-all text-gray-700 hover:scale-105 active:scale-95 border border-slate-100"
            title="Zoom out"
          >
            <FaMinus size={13} />
          </button>
        </div>

        {/* Distance, Duration & Via Stops Overview Badge */}
        {directionsInfo && (
          <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-100/90 p-3 z-10">
            <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-500 uppercase tracking-wider">
              <span>Route Overview</span>
              {directionsInfo.stopsCount > 0 && (
                <span className="bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded text-[10px] font-bold">
                  +{directionsInfo.stopsCount} stops
                </span>
              )}
            </div>
            <div className="flex items-center gap-4 mt-1.5">
              <div>
                <div className="text-sm font-black text-gray-900">
                  {directionsInfo.distance}
                </div>
                <div className="text-[10px] font-semibold text-gray-400 uppercase">Distance</div>
              </div>
              <div className="w-[1px] h-6 bg-gray-200"></div>
              <div>
                <div className="text-sm font-black text-gray-900">
                  {directionsInfo.duration}
                </div>
                <div className="text-[10px] font-semibold text-gray-400 uppercase">Est. Time</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* TRACK RIDE BUTTON */}
      {isRideStarted && (
        <div className="p-4 border-t border-slate-100 bg-white">
          <button 
            onClick={handleTrackRide}
            className="w-full bg-[#f26a21] hover:bg-orange-600 text-white py-2 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all shadow-md shadow-orange-100 active:scale-98 text-sm"
          >
            <FaMapMarkerAlt />
            Track Ride
          </button>
        </div>
      )}
    </div>
  );
}
